/**
 * browser-loop — per-session deterministic sampling.
 *
 * Closed-loop §III.8.3: sampling is per-session, NOT per-event. Per-event
 * independent sampling produces partial sessions which are useless for
 * forensics — half the events from a flow break causal joins.
 *
 * Algorithm:
 *   1. Hash `device_id || YYYY-MM-DD || sample_rate_string` → 4 bytes.
 *   2. Interpret as uint32 / 2^32 → uniform [0, 1).
 *   3. In if value < sample_rate.
 *
 * Why hash includes the date: lets a user who's been sampled-out for a
 * week get a fresh roll the next day. Without it, the same unlucky
 * users would be permanently invisible.
 *
 * dev_loop and agent_lab are 100% — sampling logic still runs for
 * uniformity but always returns true.
 *
 * Tail-promote is handled in client.ts (not here): any severity >= error
 * in a sampled-out session flips `sampledIn` true retroactively, so
 * subsequent flushes ship the ring buffer.
 */

import type { BrowserLoopConfig } from "./config";

/**
 * Cheap 32-bit FNV-1a hash. Pure browser, no crypto.subtle async dance.
 * Good enough for sampling uniformity.
 */
function fnv1a32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h >>> 0;
}

function today(): string {
  const d = new Date();
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function decideSampling(
  config: BrowserLoopConfig,
  deviceId: string,
): boolean {
  // dev_loop + agent_lab are full capture.
  if (config.mode === "dev_loop" || config.mode === "agent_lab") return true;
  if (config.mode === "off") return false;

  // rum mode — apply per-session deterministic sampling.
  const rate = config.rumSampleRate;
  if (rate <= 0) return false;
  if (rate >= 1) return true;

  const key = `${deviceId}|${today()}|${rate.toFixed(4)}`;
  const h = fnv1a32(key);
  // Map to [0, 1)
  const u = h / 0x100000000;
  return u < rate;
}
